import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { PenLine, Gift, ChevronDown, Lock } from 'lucide-react';
import api from '../../services/api';
import QrcPlayer from './QrcPlayer';

/**
 * Amène un panneau ouvert à l'écran, juste sous l'en-tête collant (.site-header), dont la
 * hauteur réelle est mesurée : une marge fixe coupait le titre de la question sur mobile
 * (retour Fabien, 29/09/2026).
 */
export function scrollToPanel(el, reduce = false) {
    if (!el) return;
    const header = document.querySelector('.site-header');
    const offset = (header ? header.getBoundingClientRect().height : 0) + 12;
    const top = el.getBoundingClientRect().top + window.scrollY - offset;
    window.scrollTo({ top: Math.max(0, top), behavior: reduce ? 'auto' : 'smooth' });
}

/**
 * Bandeau « Réponses rédigées » de l'onglet Quiz : rend visibles les sujets
 * qui proposent des QRC et monte le player INLINE dans la page (pas de popup).
 * `sujets` = sous-sujets avec qrc_count > 0 (issus de scopes/detail.php),
 * enrichis de matiere_label.
 *
 * `readOnly` (visiteur non connecté, décision Fabien du 28/09/2026) : même
 * bandeau, mais un sujet déplié liste seulement les énoncés (endpoint public
 * qrc/showcase.php, jamais la grille ni la réponse modèle) avec l'invitation à
 * créer son compte ; aucun appel au juge.
 */
export default function QrcSpotlight({ sujets, hasAccess, accentColor, scope = null, onOpenSujet = null, readOnly = false }) {
    const [openId, setOpenId] = useState(null);
    const [expanded, setExpanded] = useState(true); // déplié à l'arrivée, repliable
    const [roQuestions, setRoQuestions] = useState(null); // lecture seule : énoncés par sujet
    const panelRef = useRef(null);

    useEffect(() => {
        if (!readOnly || !scope) return;
        api.get(`/qrc/showcase.php?scope_slug=${encodeURIComponent(scope)}`)
            .then(({ data }) => setRoQuestions(data.data?.questions || []))
            .catch(() => setRoQuestions([]));
    }, [readOnly, scope]);

    // Mobile : le panneau d'un sujet s'ouvre sous la rangée de puces, hors de l'écran, et rien ne
    // semble se passer (retour Fabien, 28/09). On amène le panneau à l'écran dès qu'il est monté.
    useEffect(() => {
        if (!openId || !panelRef.current) return;
        const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const id = requestAnimationFrame(() => scrollToPanel(panelRef.current, reduce));
        return () => cancelAnimationFrame(id);
    }, [openId]);

    if (!sujets?.length) return null;

    const totalQrc = sujets.reduce((s, ss) => s + (ss.qrc_count || 0), 0);
    const hasGift = !hasAccess && sujets.some(ss => ss.qrc_has_demo);

    return (
        <div className="qrcs" style={accentColor ? { '--qrcp-accent': accentColor } : undefined}>
            <button type="button" className="qrcs-head" onClick={() => setExpanded(v => !v)} aria-expanded={expanded}>
                <span className="qrcs-ic"><PenLine size={19} /></span>
                <div className="qrcs-txt">
                    <strong>
                        Réponses rédigées
                        <span className="qrcs-new">Nouveau</span>
                    </strong>
                    <p>
                        {totalQrc} question{totalQrc > 1 ? 's' : ''} à réponse rédigée sur {sujets.length} sujet{sujets.length > 1 ? 's' : ''} —
                        réponds avec tes mots, la correction détaillée te dit précisément quoi travailler.
                        {readOnly
                            ? <> Crée ton compte gratuit, puis abonne-toi pour rédiger et être corrigé.</>
                            : hasGift && <> Des <strong>exemples corrigés offerts</strong> te montrent comment le correcteur évalue.</>}
                    </p>
                </div>
                <ChevronDown size={17} className={`qrcs-head-arrow ${expanded ? 'is-open' : ''}`} />
            </button>
            {expanded && (
            <div className="qrcs-chips">
                {sujets.map(ss => {
                    const isOpen = openId === ss.id;
                    return (
                        <button
                            key={ss.id}
                            type="button"
                            className={`qrcs-chip ${isOpen ? 'is-on' : ''}`}
                            onClick={() => setOpenId(isOpen ? null : ss.id)}
                            title={ss.matiere_label ? `${ss.matiere_label} · ${ss.label}` : ss.label}
                        >
                            <span className="qrcs-chip-label">{ss.label}</span>
                            <span className="qrcs-chip-count">{ss.qrc_count}</span>
                            {ss.qrc_has_demo && !hasAccess && !readOnly && (
                                <span className="qrcs-chip-gift"><Gift size={11} /> exemple offert</span>
                            )}
                            <ChevronDown size={13} className={`qrcs-chip-arrow ${isOpen ? 'is-open' : ''}`} />
                        </button>
                    );
                })}
            </div>
            )}
            {expanded && openId && !readOnly && (
                <div className="qrcs-player" ref={panelRef}>
                    {/* Titre du player = sujet actif (le contexte « rédigé » est déjà porté par l'encart) */}
                    <QrcPlayer key={openId} sousSujetId={openId} title={sujets.find(ss => ss.id === openId)?.label} scope={scope} onOpenSujet={onOpenSujet} />
                </div>
            )}
            {expanded && openId && readOnly && (
                <div className="qrcs-ro" ref={panelRef}>
                    {roQuestions === null && <p className="qrcs-ro-note">Chargement…</p>}
                    {roQuestions && (
                        <ul className="qrcs-ro-list">
                            {roQuestions.filter(q => q.sous_sujet_id === openId).map(q => (
                                <li key={q.id}>
                                    <Lock size={13} className="qrcs-ro-lock" />
                                    <span>{q.enonce}</span>
                                    {!!Number(q.is_demo) && <span className="qrcs-ro-demo">démo</span>}
                                </li>
                            ))}
                        </ul>
                    )}
                    <p className="qrcs-ro-note">
                        Ces questions se rédigent avec un compte et un abonnement : le correcteur rend un verdict point par point.
                        {' '}<Link to="/inscription" className="qrcs-ro-cta">Créer mon compte gratuit</Link>
                    </p>
                </div>
            )}
        </div>
    );
}
